import React, { useState, useEffect, useRef } from "react";
import SaveButtonWithModal from "./SaveButtonWithModel";
import { uploadToCloudinary } from "../utils/CloudinaryUpload";

const LOCAL_STORAGE_KEY = "productServiceFormData";

const ProductService = ({
  formData,
  onProductImagesChange,
  onFormDataChange,
  selectedOption,
  onCertificatesUrlChange,
  onIdUrlChange,
  onOptionChange,
  onSave,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCircles, setSelectedCircles] = useState({});
  const [images, setImages] = useState(Array(12).fill(null));
  const [certFiles, setCertFiles] = useState(Array(12).fill(null));
  const [showModal, setShowModal] = useState(false);
  const [idFile, setIdFile] = useState(null);
  const [idImageUrl, setIdImageUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [documentDropdownOpen, setDocumentDropdownOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState("Products / Services");
  const [boxPlaceholders, setBoxPlaceholders] = useState([
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
    "XXXXXXX",
  ]);
  const [currentBoxIndex, setCurrentBoxIndex] = useState(0);

  // Scroll functionality
  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);

  useEffect(() => {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      const parsed = JSON.parse(data);
      setImages(parsed.images || Array(12).fill(null));
      setBoxPlaceholders(parsed.boxPlaceholders || [
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
        "XXXXXXX",
      ]);
      setCurrentBoxIndex(parsed.currentBoxIndex || 0);
      setSelectedDocumentType(parsed.selectedDocumentType || "Products / Services");
    }
  }, []);

  // Calculate thumb height based on content height
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [documentDropdownOpen]);

  // Handle scroll events
  const handleScroll = () => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - thumbHeight;
    setScrollTop(scrollRatio * trackHeight);
  };

  // Mouse down on thumb
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(contentRef.current.scrollTop);

    document.addEventListener("mousemove", handleThumbMouseMove);
    document.addEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  // Mouse move when dragging thumb
  const handleThumbMouseMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = contentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  // Mouse up when dragging thumb
  const handleThumbMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleThumbMouseMove);
    document.removeEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "";
  };

  // Click on track to jump to position
  const handleTrackClick = (e) => {
    const content = contentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  const documentTypes = [
    "National Identity", "Passport", "Drivers License", "Business registration",
    "Business License", "B-BBEE- Affidavit/Certificate", "Trade certificates",
    "Health Certificate", "GAP certificate", "Training Certificate",
    "Industry-Specific Licenses or Permits", "Membership of organization",
    "Awards", "Bank Account", "Business Insurance", "Business Plan & Financial Records",
    "Title deed", "Lease agreement", "Tax Clearance", "Others"
  ];

  const handleDeleteImage = (index) => {
    setImages((prevImages) => {
      const newImages = [...prevImages];
      newImages[index] = null;
      return newImages;
    });
    setCertFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles[index] = null;
      return newFiles;
    });
    setBoxPlaceholders(prev => {
      const newPlaceholders = [...prev];
      newPlaceholders[index] = [
        "National identity card",
        "Business registration",
        "Trade Certificate",
        "Tax compliance"
      ][index % 8];
      return newPlaceholders;
    });
    if (index === currentBoxIndex) {
      setCurrentBoxIndex(index);
    }
  };

// In ProductService component
// ProductService.js
const handleImageChange = (e, index) => {
  const file = e.target.files[0];
  if (!file) return;

  const imageData = {
    previewUrl: URL.createObjectURL(file),
    file,
    name: file.name,
    type: file.type,
    size: file.size,
    documentType: boxPlaceholders[index]
  };

  setImages(prev => {
    const newImages = [...prev];
    newImages[index] = imageData;
    return newImages;
  });

  setCertFiles(prev => {
    const newFiles = [...prev];
    newFiles[index] = file;
    return newFiles;
  });

  // Immediately update parent with the new images
  const filteredImages = images.map((img, i) => 
    i === index ? imageData : img
  ).filter(img => img !== null);
  
  onProductImagesChange(filteredImages);
};

  const handleIdChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdFile(file);
      setIdImageUrl(URL.createObjectURL(file));
    }
  };

  const handleDocumentTypeSelect = (type) => {
    setSelectedDocumentType(type);
    setDocumentDropdownOpen(false);
    setBoxPlaceholders(prev => {
      const newPlaceholders = [...prev];
      newPlaceholders[currentBoxIndex] = type;
      return newPlaceholders;
    });
    setCurrentBoxIndex((prevIndex) => (prevIndex + 1) % 12);
  };

const handleSave = async () => {
  setSaving(true);
  setError("");
  
  try {
    // Upload all product images
    const uploadPromises = images
      .filter(img => img !== null)
      .map((img, index) => 
        uploadToCloudinary(img.file, {
          folder: 'products',
          public_id: `${formData.registrationNumber || 'temp'}_${index}`
        })
          .then(url => ({
            url,
            type: boxPlaceholders[index],
            documentType: img.documentType
          }))
      );

    const productUrls = await Promise.all(uploadPromises);
    
    // Update parent with the uploaded URLs
    onProductImagesChange(productUrls);

    setSaving(false);
    setShowModal(true);

    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({
        images,
        boxPlaceholders,
        currentBoxIndex,
        selectedDocumentType,
      })
    );

    onSave({
      ...formData,
      productImages: productUrls,
      typeOfOrganization: selectedOption,
    });
  } catch (error) {
    console.error("Upload error:", error);
    setError("Failed to upload product images. Please try again.");
    setSaving(false);
  }
};

  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (img && typeof img === 'string' && img.startsWith('blob:')) {
          URL.revokeObjectURL(img);
        }
      });
      if (idImageUrl) URL.revokeObjectURL(idImageUrl);
    };
  }, [images, idImageUrl]);

  return (
<div className="md:mt-6" >
      <div className="">
      
        <div className="grid grid-cols-4 md:gap-2 gap-1 mb-3 md:mb-6">
        {images.slice(0, 12).map((imgSrc, index) => (
            <div key={index} className="flex flex-col md:mt-6">
              <p className="text-[6px] md:text-[20px] font-bold  md:mb-3 text-left min-h-[12px] md:w-[260px] leading-tight">
                {boxPlaceholders[index]}
              </p>
              <div className="relative bg-[#D9D9D9] rounded-[5px] h-[85px] md:h-[400px] w-full overflow-hidden">
  {imgSrc && (
    <img
      src={imgSrc.previewUrl || imgSrc} // Handle both cases where imgSrc might be an object or string
      alt="Certificate"
      className="object-cover w-full h-full"
    />
  )}


</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-4 md:gap-2 gap-1 mb-3 md:mb-6">
        {images.slice(0, 4).map((imgSrc, index) => (
            <div key={index} className="flex flex-col md:mt-6">
              <p className="text-[6px] md:text-[24px] font-bold  md:mb-3 text-left min-h-[12px] md:w-[260px] leading-tight">
                {boxPlaceholders[index]}
              </p>
              <div className="relative bg-[#D9D9D9] rounded-[5px] h-[85px] md:h-[400px] w-full overflow-hidden">
  {imgSrc && (
    <img
      src={imgSrc.previewUrl || imgSrc} // Handle both cases where imgSrc might be an object or string
      alt="Certificate"
      className="object-cover w-full h-full"
    />
  )}


</div>
            </div>
          ))}
        </div>
      </div>
      {error && (
        <div className="mt-2 text-red-600 text-xs text-center">{error}</div>
      )}
   
   
    </div>
  );
};

export default ProductService;